<template lang="html">
  <div :style="`height:${windowHeight}px;width:100%`">
    <v-tooltip
      v-if="params('draft') === 'true'"
    >
      <template v-slot:activator="{ on }">
        <v-btn
          icon
          style="position:absolute;top:24px;left:24px;z-index:1"
          v-on="on"
          @click="bboxPasted()"
        >
          <v-icon>mdi-crop-free</v-icon>
        </v-btn>
      </template>
      <span>Copier la position actuelle</span>
    </v-tooltip>
    <v-progress-linear
      v-if="loading"
      style="position:absolute;z-index:1;max-width:100%;pointer-events: none;"
      indeterminate
      color="primary"
    />
    <div
      id="map"
      style="height:100%"
    />
  </div>
</template>
<script>
  import { mapState, mapGetters } from 'vuex'
  import colors from '@data-fair/components-app/colors/colors'
  import { bbox2zoom } from '@data-fair/components-app/map/utils'
  import { escape } from '@data-fair/components-app/filters/filters'
  import axios from 'axios'

  const layers = require('../assets/layers.json')
  const levels = require('../assets/levels.json')
  const quantiles = require('compute-quantiles')
  const style = require('../assets/style')

  let maplibregl
  if (process.browser) {
    maplibregl = require('maplibre-gl')
    require('maplibre-gl/dist/maplibre-gl.css')
  }

  export default {
    data: () => ({
      dataRefreshed: false,
      loading: true,
      metric: Object.assign({}, ...levels.map(l => ({ [l['source-layer']]: {} }))),
      layers: [],
      levels: null,
      nbHover: 0,
    }),
    computed: {
      ...mapState(['currentLevel', 'selected', 'numeratorValueField', 'denominatorValueField', 'conceptFilters']),
      ...mapGetters(['datasetUrl', 'dataset', 'config', 'tileserverUrl', 'codeFields', 'defaultColor']),
      minMax () {
        return Object.assign({}, ...this.levels.map(l => ({
          [l['source-layer']]: {
            min: Math.min(...Object.values(this.metric[l['source-layer']]).map(this.metricScore).filter(v => v !== null)),
            max: Math.max(...Object.values(this.metric[l['source-layer']]).map(this.metricScore).filter(v => v !== null)),
          },
        })))
      },
    },
    watch: {
      numeratorValueField (val) {
        this.loading = true
        this.dataRefreshed = false
        this.metric = Object.assign({}, ...this.levels.map(l => ({ [l['source-layer']]: {} })))
        this.refresh()
      },
      denominatorValueField (val) {
        this.loading = true
        this.dataRefreshed = false
        this.metric = Object.assign({}, ...this.levels.map(l => ({ [l['source-layer']]: {} })))
        this.refresh()
      },
      conceptFilters: {
        handler () {
          this.loading = true
          this.dataRefreshed = false
          this.metric = Object.assign({}, ...this.levels.map(l => ({ [l['source-layer']]: {} })))
          this.refresh()
        },
        deep: true,
      },
    },
    mounted () {
      this.levels = levels.filter(l => Object.keys(this.codeFields).filter(c => this.codeFields[c]).includes(l['source-layer']))
      layers.filter(l => l.type === 'fill').forEach(l => {
        l.paint['fill-color'] = this.defaultColor
      })
      this.levels.forEach((level, i) => {
        layers.forEach(layer => {
          const levelLayer = Object.assign({}, level, layer)
          if (levelLayer.id !== 'lines' && i < this.levels.length - 1) {
            levelLayer.maxzoom = this.levels[i + 1].minzoom
          } else {
            levelLayer.maxzoom = 20
          }
          levelLayer.id = levelLayer['source-layer'] + '-' + levelLayer.id
          levelLayer.source = levelLayer.source.replace('latest', this.config.year)
          this.layers.push(levelLayer)
        })
      })
      this.initMap()
      const layer = this.layers.find(l => l.type === 'fill' && this.map.getZoom() >= l.minzoom && this.map.getZoom() < l.maxzoom)
      this.$store.commit('setAny', { currentLevel: layer['source-layer'] })
      window.addEventListener('resize', () => {
        setTimeout(() => {
          this.map.resize()
        }, 100)
      }, false)
    },
    methods: {
      initMap () {
        if (!maplibregl) return
        const initialBounds = this.config.bbox && this.config.bbox.length && maplibregl.LngLatBounds.convert(JSON.parse(this.config.bbox))
        const params = new URLSearchParams(window.location.search)
        const zoom = params.get('zoom') ? Number(params.get('zoom')) : (initialBounds ? bbox2zoom(JSON.parse(this.config.bbox)) : 5)
        const center = params.get('lng') && params.get('lat') ? [Number(params.get('lng')), Number(params.get('lat'))] : (initialBounds ? initialBounds.getCenter().toArray() : [2.0833, 47.2167])

        this.map = new maplibregl.Map({
          container: 'map',
          center,
          zoom,
          style,
          transformRequest: (url, resourceType) => {
            return {
              url,
              credentials: 'include', // include cookies, for data-fair sessions
            }
          },
        })
        this.map.addControl(new maplibregl.NavigationControl(), 'top-right')

        // Disable map rotation using right click + drag
        this.map.dragRotate.disable()
        // Disable map rotation using touch rotation gesture
        this.map.touchZoomRotate.disableRotation()

        this.map.on('click', (e) => this.showTooltip([e.lngLat.lat, e.lngLat.lng]))
        this.map.on('moveend', (e) => this.moved(e))

        // Finalize map after first load
        this.map.once('load', () => {
          const labelLayer = this.map.getStyle().layers.find(l => l['source-layer'] === 'place').id

          this.layers.forEach((layer, i, s) => {
            this.map.addLayer(layer, (i % 3 !== 0) ? labelLayer : undefined)
            this.map.on('mouseenter', layer.id, (e) => {
              this.nbHover++

              this.map.getCanvas().style.cursor = 'pointer'
            })
            this.map.on('mouseleave', layer.id, () => {
              this.nbHover--
              if (!this.nbHover) this.map.getCanvas().style.cursor = ''
            })
          })

          const code = params.get('selected')
          if (code) {
            setTimeout(() => {
              const division = this.map.querySourceFeatures('admin-divs-' + this.config.year, { sourceLayer: [this.currentLevel], filter: ['in', 'code', code] }).pop()
              this.map.setFilter(this.currentLevel + '-lines-selected', ['in', 'code', code])
              let metric
              if (this.metric[this.currentLevel][code]) {
                metric = this.metric[this.currentLevel][code]
              }
              this.$store.commit('setAny', { selected: Object.assign({ metric }, division.properties) })
            }, 1000)
          }
        })

        // "idle" event seems to be the best indication that the map is fully loaded
        this.map.on('idle', async () => {
          if (!this.dataRefreshed) {
            await this.refresh()
            if (window.triggerCapture) setTimeout(() => window.triggerCapture(), 10000)
          } else {
            this.loading = false
            if (window.triggerCapture) setTimeout(() => window.triggerCapture(), 5000)
          }
        })
      },
      metricScore (metric) {
        return (this.config.denominator.type !== 'none' && metric.denominator) ? (metric.numerator / metric.denominator) : metric.numerator
      },
      moved (e) {
        const params = new URLSearchParams(window.location.search)
        params.set('zoom', e.target.getZoom())
        const { lat, lng } = e.target.getCenter()
        params.set('lng', lng)
        params.set('lat', lat)
        window.history.replaceState(null, null, window.location.pathname + '?' + params.toString())
        if (e.originalEvent) {
          this.loading = true
          this.dataRefreshed = false
        }
      },
      showTooltip (pos) {
        this.tooltipPos = pos
        const lngLat = [this.tooltipPos[1], this.tooltipPos[0]]

        if (this.popup) this.popup.remove()
        const division = this.map.queryRenderedFeatures(this.map.project(lngLat), {
          layers: [this.currentLevel + '-fill'],
        }).pop()
        if (division) {
          const params = new URLSearchParams(window.location.search)
          if (!this.selected || this.selected.code !== division.properties.code) {
            this.map.setFilter(this.currentLevel + '-lines-selected', ['in', 'code', division.properties.code])
            let metric
            if (this.metric[this.currentLevel][division.properties.code]) {
              metric = this.metric[this.currentLevel][division.properties.code]
            }
            this.$store.commit('setAny', { selected: Object.assign({ metric }, division.properties) })
            params.set('selected', division.properties.code)
          } else {
            this.map.setFilter(this.currentLevel + '-lines-selected', ['in', 'code', ''])
            this.$store.commit('setAny', { selected: null })
            params.delete('selected')
          }
          window.history.replaceState(null, null, window.location.pathname + '?' + params.toString())
        }
      },
      bboxPasted () {
        const bbox = JSON.stringify([].concat(...this.map.getBounds().toArray()).map(n => n.toFixed(6)))
        navigator.clipboard.writeText(bbox)
        if (window.parent) window.parent.postMessage({ type: 'set-config', content: { field: 'bbox', value: bbox } }, '*')
      },
      async refresh () {
        if (!this.map.isStyleLoaded()) return
        const layer = this.layers.find(l => l.type === 'fill' && this.map.getZoom() >= l.minzoom && this.map.getZoom() < l.maxzoom)
        if (layer) {
          const level = layer['source-layer']
          if (level !== this.currentLevel) {
            this.map.setFilter(this.currentLevel + '-lines-selected', ['in', 'code', ''])
            this.$store.commit('setAny', { selected: null, currentLevel: level })
          }

          const divisions = this.map.querySourceFeatures('admin-divs-' + this.config.year, { sourceLayer: level })
          const ids = divisions.map(d => d.properties.code).filter(id => !this.metric[level][id])

          const numeratorData = await this.getData(level, ids, this.config.numerator.type, this.numeratorValueField && this.numeratorValueField.key, this.config.numerator.propertyField, this.config.numerator.propertyValue)
          if (this.config.denominator.type !== 'none') {
            const denominatorData = await this.getData(level, ids, this.config.denominator.type, this.denominatorValueField && this.denominatorValueField.key, this.config.denominator.propertyField, this.config.denominator.propertyValue)
            Object.entries(denominatorData).forEach(([code, value]) => {
              this.$set(this.metric[level], code, { denominator: value, numerator: numeratorData[code] || 0 })
            })
          } else {
            Object.entries(numeratorData).forEach(([code, value]) => {
              this.$set(this.metric[level], code, { numerator: value })
            })
          }
          if (level === 'departements' && this.metric[level]['69']) {
            this.$set(this.metric[level], '69M', this.metric[level]['69'])
            this.$set(this.metric[level], '69D', this.metric[level]['69'])
          }

          // We set metric to null to avoid fetching it again
          ids.forEach(id => {
            if (!this.metric[level][id]) this.$set(this.metric[level], id, { numerator: null, denominator: null })
          })
          const groups = []
          let start = this.minMax[level].min
          if (this.minMax[level].min === this.minMax[level].max) {
            groups.push({
              min: start,
              max: start,
            })
          } else {
            if (this.config.ranges.type === 'constant') {
              const incr = (this.minMax[level].max - this.minMax[level].min) / this.config.colorscheme.colorsNumber
              while (start < this.minMax[level].max && groups.length < this.config.colorscheme.colorsNumber) {
                groups.push({
                  min: start,
                  max: start + incr,
                })
                start += incr
              }
              groups[groups.length - 1].max = this.minMax[level].max
            } else if (this.config.ranges.type === 'quantiles') {
              const arr = Object.values(this.metric[level]).map(this.metricScore).filter(v => v !== null)
              const q = quantiles(arr, this.config.colorscheme.colorsNumber)
              for (let i = 0; i < q.length - 1; i++) {
                groups.push({
                  min: q[i],
                  max: q[i + 1],
                })
              }
            } else {
              this.config.ranges.groups.forEach(g => groups.push(g))
            }
          }
          const groupsColors = colors(this.config.colorscheme, groups.map((g, i) => i))
          const legend = groups.map((g, i) => [g, groupsColors[i]])
          this.$store.commit('setAny', { legend })

          const idColor = {}
          const expression = ['match', ['get', 'code']]
          divisions.forEach(d => {
            let metric = null
            if (this.metric[level][d.properties.code]) {
              metric = this.metric[level][d.properties.code]
            }
            if (metric && metric.numerator !== null) {
              const computedMetric = (this.config.denominator.type !== 'none' && metric.denominator) ? (metric.numerator / metric.denominator) : metric.numerator
              const g = legend.find(g => computedMetric >= g[0].min && computedMetric <= g[0].max)
              idColor[d.properties.code] = (g && g[1]) || this.defaultColor
            }
          })
          Object.keys(idColor).forEach(id => expression.push(id, idColor[id]))
          expression.push(this.defaultColor)
          this.map.setPaintProperty(level + '-fill', 'fill-color', expression)
        }
        this.dataRefreshed = true
        this.loading = false
      },
      params (p) {
        const params = new URLSearchParams(window.location.search)
        return params.get(p)
      },
      async getData (level, ids, metric, metricField, propertyField, propertyValue) {
        const params = {
          field: this.codeFields[level].key,
          agg_size: 1000,
          ...this.conceptFilters,
          finalizedAt: this.dataset.finalizedAt,
        }
        if (metric !== 'count') {
          params.metric = metric
          params.metric_field = metricField
        }
        if (level === 'communes' || level === 'iris') {
          const prefixes = {}
          let sum = 0
          ids.forEach(id => {
            const code = id.substring(0, 2)
            prefixes[code] = (prefixes[code] || 0) + 1
            sum += 1
          })
          if (sum > 0) {
            const codes = Object.keys(prefixes) // .filter(p => prefixes[p] / sum > 0.03)
            const aggsArr = await Promise.all(codes.map(id => {
              let qs = escape(this.codeFields[level].key) + ':'
              if (this.codeFields[level].type === 'string') qs += '/' + id + '.*' + '/'
              else if (this.codeFields[level].type === 'integer') {
                const idStart = id * 1000
                qs += `[${idStart} TO ${idStart + 1000}}`
              }
              if (propertyField) {
                qs += ' AND ' + propertyField + `:"${propertyValue}"`
              }
              return axios.get(this.datasetUrl + '/values_agg', {
                params: Object.assign({ qs }, params),
              })
            }))
            const ret = {}
            aggsArr.forEach(agg => {
              agg.data.aggs.forEach(a => {
                ret[a.value] = metric !== 'count' ? a.metric : a.total
              })
            })
            return ret
          }
        } else {
          if (level === 'departements' && (ids.includes('69M') || ids.includes('69D'))) {
            ids.push('69')
          }
          const filterIds = ids.filter(id => this.codeFields[level].type === 'string' || !!Number(id))
          if (filterIds.length) {
            if (propertyField) {
              params[`${propertyField}_in`] = propertyValue
            }
            const buckets = []
            while (filterIds.length) {
              buckets.push(filterIds.splice(0, 500))
            }
            const aggsArr = await Promise.all(buckets.map(bucket => {
              params[`${this.codeFields[level].key}_in`] = bucket.join(',')
              return axios.get(this.datasetUrl + '/values_agg', {
                params,
              })
            }))
            const ret = {}
            aggsArr.forEach(agg => {
              agg.data.aggs.forEach(a => {
                ret[a.value] = metric !== 'count' ? a.metric : a.total
              })
            })
            return ret
          }
        }
        return {}
      },
    },
  }
</script>

<style lang="css">
</style>
